import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import EmailAddress from "_shared/email-addresses/email-address";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import Name from "_shared/names/name";
import Company from "marketplace/companies/company";
import CompanyProfile from "marketplace/companies/profiles/company-profile";
import IndividualSchema, {
  IndividualConstructorParams,
  IndividualJSON,
  IndividualParams,
} from "marketplace/individuals/_schema/individual-schema";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import UserInformation from "users/information/user-information";
import { IRedlineableField } from "work/proposals/redlining/field-redlines/field-redline";

export default class Individual implements IRedlineableField {
  public static get EXAMPLE(): Individual {
    return new Individual({
      id: Guid.generate(),
      userId: Guid.generate(),
      profile: IndividualProfile.EXAMPLE,
      company: Company.EXAMPLE,
      isVisible: true,
    });
  }

  public static create(values: IndividualParams): Individual {
    const parsed = IndividualSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid Individual parameters.");
    }
    return new Individual(parsed);
  }

  public static fromJSON(json: IndividualJSON | null | undefined): Individual | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const parsed = IndividualSchema.deserialize(json);
    return new Individual(parsed);
  }

  public static fromJSONArray(
    jsonArray: (IndividualJSON | null | undefined)[] | null | undefined,
    options?: { allowEmpty: boolean },
  ): Individual[] | null | undefined {
    if (isNullOrUndefined(jsonArray)) return jsonArray;

    const opts = options ?? { allowEmpty: false };
    const individuals: Individual[] = [];

    for (const json of jsonArray) {
      if (isNullOrUndefined(json)) {
        if (!opts.allowEmpty) {
          throw new InvalidIndividualJSONError({
            value: jsonArray,
            message: "Invalid Individual JSON in array (null/undefined entry)",
          });
        }
        continue;
      }

      try {
        const individual = Individual.fromJSON(json);
        if (individual) {
          individuals.push(individual);
        }
      } catch (err) {
        if (!opts.allowEmpty) {
          throw new InvalidIndividualJSONError({
            value: jsonArray,
            message: (err as Error)?.message ?? "Invalid Individual JSON in array",
          });
        }
      }
    }

    return individuals;
  }

  public readonly id: Guid;
  public readonly userId: Guid;
  public readonly profile: IndividualProfile;
  public readonly company: Company;
  public readonly hasSigningAuthority?: boolean; // Optional property to indicate if the individual has signing authority for the legal entity

  public get isEmpty(): boolean {
    // An individual is never empty, required for IRedlineableField
    return false;
  }
  public get isVisible(): boolean {
    return this._isVisible;
  }
  public get legalEntityId(): Guid {
    return this.company.legalEntityId;
  }
  public get entityName(): string | undefined {
    return this.company.profile?.name;
  }
  public get firstName(): string {
    return this.profile.firstName;
  }
  public get lastName(): string {
    return this.profile.lastName;
  }
  public get fullName(): string {
    return this.profile?.fullName;
  }
  public get initials(): string {
    return `${this.profile.firstName[0]}${this.profile.lastName[0]}`;
  }
  public get email(): EmailAddress | undefined {
    return this.profile.email;
  }
  public toJSON(): IndividualJSON {
    return IndividualSchema.serialize(this);
  }

  public constructor(values: IndividualConstructorParams) {
    const { id, userId, isVisible, hasSigningAuthority, ...rest } = values;

    // Extract company, entityId, entityName, profile, firstName, lastName safely
    const company = (rest as { company?: Company }).company;
    const legalEntityId = (rest as { legalEntityId?: Guid }).legalEntityId;
    const entityName = (rest as { entityName?: string }).entityName;
    const profile = (rest as { profile?: IndividualProfile }).profile;
    const firstName = (rest as { firstName?: string }).firstName;
    const lastName = (rest as { lastName?: string }).lastName;

    this.id = id;
    this.userId = userId;
    this.profile = this._buildProfile(profile, firstName, lastName);
    this.company = this._buildCompany(company, legalEntityId, entityName);
    this.hasSigningAuthority = hasSigningAuthority;

    this._isVisible = isVisible ?? false;
  }

  public toString(): string {
    return this.fullName;
  }

  public isEqualTo(other: Individual | null | undefined): boolean {
    return (
      !!other &&
      this.id.isEqualTo(other.id) &&
      this.userId.isEqualTo(other.userId) &&
      this.legalEntityId.isEqualTo(other.legalEntityId)
    );
  }

  public toEntityMember(): EntityMember {
    return new EntityMember({
      legalEntityId: this.legalEntityId,
      userId: this.userId,
    });
  }

  public toUserInformation(): UserInformation {
    if (isNullOrUndefined(this.userId)) {
      throw new Error("Individual.toUserInformation: userId is null or undefined.");
    }
    if (isNullOrUndefined(this.legalEntityId)) {
      throw new Error("Individual.toUserInformation: legalEntityId is null or undefined.");
    }
    if (isNullOrUndefined(this.firstName) || isNullOrUndefined(this.lastName)) {
      throw new Error("Individual.toUserInformation: firstName/lastName is null or undefined.");
    }
    return UserInformation.create({
      id: this.userId,
      legalEntityId: this.legalEntityId,
      email: this.email,
      name: Name.create({ params: { firstName: this.firstName, lastName: this.lastName } }),
      isVendor: false,
      isClient: false,
      isVendorRepresentative: false,
      isClientRepresentative: false,
      viewingAsVendor: false,
      isSuperUser: false,
      individualId: this.id,
      companyId: this.company?.id,
      companyName: this.entityName,
    });
  }

  public toggleVisibility(): boolean {
    this._isVisible = !this._isVisible;
    return this._isVisible;
  }

  private _isVisible: boolean = false;

  private _buildProfile(
    profile: IndividualProfile | undefined,
    firstName: string | undefined,
    lastName: string | undefined,
  ): IndividualProfile {
    if (profile) return new IndividualProfile(profile);
    else if (firstName && lastName)
      return new IndividualProfile({
        firstName: firstName,
        lastName: lastName,
      });
    else throw new Error("Either profile or firstName and lastName must be provided to create Individual");
  }

  private _buildCompany(
    company: Company | undefined,
    legalEntityId: Guid | undefined,
    entityName: string | undefined,
  ): Company {
    if (company) return company;
    else if (legalEntityId)
      return new Company({
        id: Guid.generate(),
        legalEntityId,
        profile: entityName ? CompanyProfile.create({ name: entityName, categories: [] }) : undefined,
      });
    else throw new Error("Either company or legalEntityId must be provided to create Individual");
  }
}
export class InvalidIndividualJSONError extends Error {
  constructor(context: {
    value: string | Array<IndividualJSON | null | undefined> | null | undefined;
    message?: string;
  }) {
    super(
      "Invalid Individual JSON" +
        (context.value ? ": " + JSON.stringify(context.value) : "") +
        (context.message ? " -\n" + context.message : ""),
    );
  }
}
